import { GridApi, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';

interface RemoteChangeFlashOptions<TRow> {
  readonly getApi: () => GridApi<TRow> | null;
  /** Fields to compare and flash — the value columns that opted into the effect
   *  (enableCellChangeFlash). Read per fetch so column toggles stay honoured. */
  readonly getFlashFields: () => string[];
  /** Must mirror the grid's getRowId — the diff pairs each incoming row with the node it replaces. */
  readonly getRowKey: (row: TRow) => string;
}

/** SSRM's refreshServerSide swaps block data in place without ever flashing — AG Grid only
 *  flashes on transactions and setDataValue, so enableCellChangeFlash alone never fires on
 *  these grids. Wrapping the datasource closes the gap: each incoming row is diffed against
 *  the row node it replaces, and the value cells a remote recalc moved flash their
 *  remote-update green (same pacing as the campaign grid, via the grid's flash options). */
export function withRemoteChangeFlash<TRow>(
  datasource: IServerSideDatasource,
  options: RemoteChangeFlashOptions<TRow>,
): IServerSideDatasource {
  return {
    getRows: (params: IServerSideGetRowsParams) => {
      datasource.getRows({
        ...params,
        success: info => {
          const api = options.getApi();
          const fields = options.getFlashFields();
          // Cell-precise diff: a row where only the value moved must not drag the eye to
          // its untouched (often empty) sibling columns — each field flashes its own rows.
          const changedKeysByField = new Map<string, string[]>();
          if (api && fields.length) {
            for (const row of (info.rowData ?? []) as TRow[]) {
              const previous = api.getRowNode(options.getRowKey(row))?.data as Record<string, unknown> | undefined;
              if (!previous) continue;
              const next = row as Record<string, unknown>;
              for (const field of fields) {
                if (!Object.is(previous[field] ?? null, next[field] ?? null)) {
                  const keys = changedKeysByField.get(field) ?? [];
                  keys.push(options.getRowKey(row));
                  changedKeysByField.set(field, keys);
                }
              }
            }
          }
          params.success(info);
          if (api && changedKeysByField.size) {
            // Next tick: the grid must commit the new block before the flash targets its nodes.
            // A navigation can land in between — a destroyed grid absorbs the flash silently.
            setTimeout(() => {
              if (api.isDestroyed()) return;
              for (const [field, keys] of changedKeysByField) {
                const rowNodes = keys.map(key => api.getRowNode(key)).filter(node => node !== undefined);
                if (rowNodes.length) api.flashCells({ rowNodes, columns: [field] });
              }
            });
          }
        },
      });
    },
  };
}
